import { useState } from 'react';
import { useI18n } from '../i18n';
import { useAccount } from '../account';
import { useConnections, useConnectionsServer, connEndlessStats, type ConnGroup, type ConnState } from '../lib/connections';
import { localName, flagSrc, byCca3 } from '../lib/countries';
import { buildPath } from '../router';
import { buildConnectionsText } from '../lib/share';
import { makeConnectionsImage } from '../lib/shareImage';
import Countdown from '../components/Countdown';
import ShareModal from '../components/ShareModal';
import { IconShuffle } from '../components/Icons';
import { ConnectionsSkeleton } from '../components/Skeleton';

const SYMBOL = ['', '●', '▲', '◆', '✦']; // tier 1..4

function ConnectionsBoard({ g, endless }: { g: ConnState; endless: boolean }) {
  const { t, lang } = useI18n();
  const [share, setShare] = useState(false);
  const done = g.status === 'won' || g.status === 'lost';

  if (g.status === 'loading' || !g.cats) return <ConnectionsSkeleton />;

  const title = (catId: string) => g.cats![catId]?.names[lang] ?? catId;
  // 结束时 g.locked 含全部 4 组 → 每国可映射到难度色
  const tierOf = (cca3: string) => g.locked.find((grp) => grp.members.includes(cca3))?.tier ?? 1;

  function LockedBar({ group }: { group: ConnGroup }) {
    return (
      <div className={`conn-lock t${group.tier}`}>
        <span className="conn-sym" aria-hidden="true">
          {SYMBOL[group.tier]}
        </span>
        <b className="conn-lock-title">{title(group.catId)}</b>
        <span className="conn-lock-row">
          {group.members.map((m) => {
            const c = byCca3(m);
            return c ? <img key={m} src={flagSrc(c)} alt={localName(c, lang)} title={localName(c, lang)} /> : null;
          })}
        </span>
      </div>
    );
  }

  return (
    <div className="conn">
      {!endless && <Countdown />}

      <div className="conn-locked">
        {g.locked.map((group) => (
          <LockedBar key={group.catId} group={group} />
        ))}
      </div>

      {!done && (
        <>
          <div className={`conn-grid${g.shake ? ' shake' : ''}`}>
            {g.tiles.map((cca3) => {
              const c = byCca3(cca3);
              if (!c) return null;
              const idx = g.selected.indexOf(cca3);
              return (
                <button
                  key={cca3}
                  className={`conn-tile${idx >= 0 ? ' on' : ''}`}
                  onClick={() => g.toggle(cca3)}
                  aria-pressed={idx >= 0}
                >
                  <img src={flagSrc(c)} alt="" />
                  <span className="conn-name">{localName(c, lang)}</span>
                  {idx >= 0 && <i className="conn-num">{idx + 1}</i>}
                </button>
              );
            })}
          </div>

          <div className="conn-toast" aria-live="polite">
            {g.oneAway ? t.conn.oneAway : ''}
          </div>

          <div className="conn-bar">
            <div className="conn-mistakes" title={t.conn.mistakes}>
              <span className="conn-mistakes-label">{t.conn.mistakes}</span>
              {[0, 1, 2, 3].map((i) => (
                <i key={i} className={i < 4 - g.mistakes ? 'on' : ''} />
              ))}
            </div>
            <div className="conn-actions">
              <button className="conn-btn" onClick={g.shuffle}>
                <IconShuffle width={14} height={14} />
                {t.conn.shuffle}
              </button>
              <button className="conn-btn" onClick={g.deselect} disabled={!g.selected.length}>
                {t.conn.deselect}
              </button>
              <button className="conn-btn primary" onClick={g.submit} disabled={g.selected.length !== 4}>
                {t.conn.submit}
              </button>
            </div>
          </div>
        </>
      )}

      {done && <ConnResult g={g} endless={endless} tierOf={tierOf} onShare={() => setShare(true)} />}

      {share && (
        <ShareModal
          text={buildConnectionsText(g.num, g.submissions, tierOf, g.status === 'won')}
          url={'https://geodle.org' + buildPath(lang, 'connections', endless)}
          makeImage={() => makeConnectionsImage({ num: g.num, submissions: g.submissions, tierOf, won: g.status === 'won' })}
          onClose={() => setShare(false)}
        />
      )}
    </div>
  );
}

const TIER_RING = ['', '#b8860b', '#3d6a30', '#355058', '#893420']; // tier 1..4

// 战报卡:色块回放网格 + 统计 + 分享/下一题。
function ConnResult({
  g,
  endless,
  tierOf,
  onShare,
}: {
  g: ConnState;
  endless: boolean;
  tierOf: (cca3: string) => number;
  onShare: () => void;
}) {
  const { t } = useI18n();
  const { me } = useAccount();
  const won = g.status === 'won';
  const perfect = won && g.mistakes === 0;
  const led = endless ? connEndlessStats() : null;
  const dailyStreak = !endless && me?.uid && me?.connStreak ? me.connStreak.current : null;

  return (
    <div className={`conn-result ${won ? 'win' : 'lose'}${perfect ? ' perfect' : ''}`}>
      <div className="conn-result-emblem" aria-hidden="true">
        {won ? '✦' : '✕'}
      </div>
      <h2>{won ? t.conn.solved : t.conn.lost}</h2>
      <p className="conn-result-sub">{won ? (perfect ? t.conn.flawless : t.conn.wellPlayed) : t.conn.lostSub}</p>

      <div className="conn-recap" aria-hidden="true">
        {g.submissions.map((row, i) => (
          <div className="conn-recap-row" key={i}>
            {row.map((cca3, j) => (
              <i key={j} className="conn-recap-cell" style={{ background: TIER_RING[tierOf(cca3)] || '#cbbd95' }} />
            ))}
          </div>
        ))}
      </div>

      <div className="conn-result-stats">
        {endless && led ? (
          <>
            <div>
              <b>{led.streak}</b>
              <span>{t.streak}</span>
            </div>
            <div>
              <b>{led.best}</b>
              <span>{t.best}</span>
            </div>
            <div>
              <b>{g.mistakes}</b>
              <span>{t.conn.mistakes}</span>
            </div>
          </>
        ) : (
          <>
            <div>
              <b>{g.submissions.length}</b>
              <span>{t.conn.guesses}</span>
            </div>
            <div>
              <b>{g.mistakes}</b>
              <span>{t.conn.mistakes}</span>
            </div>
            {dailyStreak !== null && (
              <div>
                <b>{dailyStreak}</b>
                <span>{t.streak}</span>
              </div>
            )}
          </>
        )}
      </div>

      <div className="round-actions">
        {endless && (
          <button className="share-trigger ghost" onClick={g.next}>
            {t.conn.nextPuzzle} →
          </button>
        )}
        <button className="share-trigger" onClick={onShare}>
          {t.share}
        </button>
      </div>
    </div>
  );
}

function ConnLocal({ endless }: { endless: boolean }) {
  return <ConnectionsBoard g={useConnections(endless)} endless={endless} />;
}
function ConnServer({ onOffline }: { onOffline: () => void }) {
  return <ConnectionsBoard g={useConnectionsServer(onOffline)} endless={false} />;
}

// 入口:无尽=本地;每日+后端在线=服务器权威(竞技);每日+离线=本地练习。
export default function ConnectionsMode({ endless = false }: { endless?: boolean }) {
  const { me, loading } = useAccount();
  const [offline, setOffline] = useState(false);

  if (endless) return <ConnLocal endless />;
  if (loading) return <ConnectionsSkeleton />;
  if (me && me.uid && !offline) return <ConnServer onOffline={() => setOffline(true)} />;
  return <ConnLocal endless={false} />;
}
